import { StanTextArea } from '@/components';

import { useStoreItem } from '@/features/theme-editor/hooks/useStoreItem';
import { EditorialTextDataSchema } from '@/features/theme-editor/schema/store';
import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { EditorialTextItem } from '@/features/theme-editor/types/store';
import z from 'zod';
import { Panel, PanelFooter } from '../../Panel';
import { Label } from '../../ui/Label';

interface Props {
    id: number;
}

const EditorialTextDataEditSchema = EditorialTextDataSchema.extend({
    editorial_text: z.object({
        primary_text: z.string().optional(),
        secondary_text: z.string().optional(),
    }),
});

export const EditorialText = ({ id }: Props) => {
    const item = useStoreItem(id);
    const { updateItem } = useCustomizationStore();

    const validation = EditorialTextDataEditSchema.safeParse(item?.data ?? { editorial_text: {} });
    if (!item || !validation.success) {
        console.error('EditorialText data validation failed:', validation.error);
        return null;
    }

    const data = validation.data;
    const { primary_text, secondary_text } = data.editorial_text ?? {};
    const maxPrimaryCount = 120;
    const maxAdditionalCount = 240;

    return (
        <Panel footer={<PanelFooter id={id} />}>
            <Label
                label='Primary text'
                required={!EditorialTextDataSchema.shape.editorial_text.shape.primary_text.isOptional()}
                currentCount={primary_text?.length?.toString() ?? '0'}
                totalCount={maxPrimaryCount.toString()}
            >
                <StanTextArea
                    id='primary'
                    onChange={event => {
                        const value = event.target.value.slice(0, maxPrimaryCount);
                        updateItem({
                            ...item,
                            data: { ...data, editorial_text: { ...data.editorial_text, primary_text: value } },
                        } as EditorialTextItem);
                    }}
                    value={primary_text}
                    rows={3}
                />
            </Label>
            <Label
                label='Additional text'
                required={!EditorialTextDataSchema.shape.editorial_text.shape.secondary_text.isOptional()}
                currentCount={secondary_text?.length?.toString() ?? '0'}
                totalCount={maxAdditionalCount.toString()}
            >
                <StanTextArea
                    id='additional'
                    onChange={event => {
                        const value = event.target.value.slice(0, maxAdditionalCount);
                        updateItem({
                            ...item,
                            data: { ...data, editorial_text: { ...data.editorial_text, secondary_text: value } },
                        } as EditorialTextItem);
                    }}
                    value={secondary_text}
                    rows={3}
                />
            </Label>
        </Panel>
    );
};
